"use client";

import { useState, useEffect, useRef } from "react";
import { Briefcase, Search, ChevronDown, Check, Plus, Loader2 } from "lucide-react";
import { TRADES as STATIC_TRADES } from "@/lib/trades";
import { api } from "@/lib/api";

interface TradeSearchProps {
  value: string;
  onChange: (value: string) => void;
}

export default function TradeSearch({ value, onChange }: TradeSearchProps) {
  const [query, setQuery] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [dynamicTrades, setDynamicTrades] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function fetchTrades() {
      try {
        setLoading(true);
        const res = await api.getTrades();
        setDynamicTrades(res as string[]);
      } catch (e) {
        console.error("Failed to fetch trades", e);
      } finally {
        setLoading(false);
      }
    }
    fetchTrades();
  }, []);

  const allTrades = Array.from(new Set([...STATIC_TRADES, ...dynamicTrades])).sort();

  const filteredTrades = allTrades.filter(t => 
    t.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 100);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (trade: string) => {
    onChange(trade);
    setQuery(trade);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative group">
        <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" size={18} />
        <input
          type="text"
          placeholder="Type or search your professional trade..."
          className="w-full rounded-xl border border-slate-200 bg-white py-3.5 pl-11 pr-10 text-sm outline-none ring-blue-100 transition-all focus:border-blue-600 focus:ring-4"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
        <ChevronDown 
          className={`absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} 
          size={18} 
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl animate-in fade-in slide-in-from-top-2">
          <div className="max-h-64 overflow-y-auto">
            {filteredTrades.length > 0 ? (
              filteredTrades.map((trade, i) => (
                <button
                  key={i}
                  type="button"
                  className="flex w-full items-center justify-between p-3.5 text-left hover:bg-blue-50 transition-colors"
                  onClick={() => handleSelect(trade)}
                >
                  <span className={`text-sm ${value === trade ? "font-bold text-blue-600" : "text-slate-700"}`}>
                    {trade}
                  </span>
                  {value === trade && <Check size={16} className="text-blue-600" />}
                </button>
              ))
            ) : query && (
              <button
                type="button"
                className="flex w-full items-center gap-3 p-4 text-left hover:bg-emerald-50 transition-colors group"
                onClick={() => handleSelect(query)}
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 group-hover:bg-emerald-200">
                  <Plus size={16} />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">Add "{query}"</p>
                  <p className="text-xs text-slate-500">Doesn't match our list? Use this custom trade.</p>
                </div>
              </button>
            )}
          </div>
          {filteredTrades.length > 0 && query && !STATIC_TRADES.some(t => t.toLowerCase() === query.toLowerCase()) && (
            <div className="border-t border-slate-100 p-2">
               <button
                type="button"
                className="flex w-full items-center gap-2 rounded-lg p-2 text-left hover:bg-emerald-50 transition-colors group"
                onClick={() => handleSelect(query)}
              >
                <Plus size={14} className="text-emerald-600" />
                <span className="text-xs font-semibold text-slate-600">Use custom: "{query}"</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
